import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useDeferredValue,
} from 'react';
import { FixedSizeList } from 'react-window';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  TextField,
  InputAdornment,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Paper,
  useMediaQuery,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import ViewerDialogFrame from './ViewerDialogFrame';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Code as CodeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FormatIndentIncrease as FormatIndentIncreaseIcon,
  DataObject as DataObjectIcon,
  DataArray as DataArrayIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerData } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '90vw',
    height: '90vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const JsonContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
}));

const JsonPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const StatsCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '6px',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  margin: '8px 12px 0 12px',
  [theme.breakpoints.down('sm')]: {
    margin: '6px 8px 0 8px',
  },
}));

const FilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px 20px',
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
    gap: '12px',
  },
}));

const JsonViewer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  padding: '20px',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
}));

const JsonContent = styled(Paper)(({ theme }) => ({
  padding: '20px',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  fontFamily: 'monospace',
  fontSize: '14px',
  lineHeight: '1.5',
  overflow: 'hidden',
  height: '100%',
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  boxSizing: 'border-box',
}));

const RawJsonContent = styled('pre')({
  margin: 0,
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  overflow: 'auto',
  whiteSpace: 'pre',
  boxSizing: 'border-box',
});

const JsonKey = styled('span')(({ theme }) => ({
  color: '#0066cc',
  fontWeight: 'bold',
}));

const JsonString = styled('span')(({ theme }) => ({
  color: '#d73a49',
}));

const JsonNumber = styled('span')(({ theme }) => ({
  color: '#005cc5',
}));

const JsonBoolean = styled('span')(({ theme }) => ({
  color: '#e36209',
}));

const JsonNull = styled('span')(({ theme }) => ({
  color: '#6f42c1',
}));

const JsonBracket = styled('span')(({ theme }) => ({
  color: '#24292e',
  fontWeight: 'bold',
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  width: '24px',
  height: '24px',
  padding: 0,
  marginLeft: '8px',
  fontSize: '12px',
  verticalAlign: 'middle',
  flexShrink: 0,
}));

// Keep the virtualized row height larger than the rendered text and controls.
// String values are escaped before rendering so embedded newlines cannot make
// one logical JSON row taller than the space reserved by react-window.
const JSON_ROW_HEIGHT = 34;
const JSON_LIST_OVERSCAN = 8;

const isJsonContainer = (value) => value !== null && typeof value === 'object';

const getJsonPath = (parentPath, key, isArray) => (
  isArray
    ? `${parentPath}[${key}]`
    : (parentPath ? `${parentPath}.${key}` : key)
);

const getJsonSearchableValue = (value) => {
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const EMPTY_SEARCH_INFO = {
  active: false,
  containsMatchPaths: new Set(),
  expandedPaths: new Set(),
  labelMatchPaths: new Set(),
  valueMatchPaths: new Set(),
  matchCount: 0,
};

const buildJsonSearchInfo = (value, searchTerm) => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const info = {
    active: Boolean(normalizedSearchTerm),
    containsMatchPaths: new Set(),
    expandedPaths: new Set(),
    labelMatchPaths: new Set(),
    valueMatchPaths: new Set(),
    matchCount: 0,
  };
  if (!info.active) return info;

  const visit = (currentValue, path, label, ancestors) => {
    const labelMatches = label !== null
      && String(label).toLowerCase().includes(normalizedSearchTerm);
    const searchableValue = getJsonSearchableValue(currentValue);
    const valueMatches = searchableValue.toLowerCase().includes(normalizedSearchTerm);
    let descendantMatches = false;

    if (isJsonContainer(currentValue)) {
      const isArray = Array.isArray(currentValue);
      const keys = isArray ? null : Object.keys(currentValue);
      const entryCount = isArray ? currentValue.length : keys.length;
      for (let index = 0; index < entryCount; index += 1) {
        const key = isArray ? index : keys[index];
        const childPath = getJsonPath(path, key, isArray);
        ancestors.push(path);
        const childMatches = visit(currentValue[key], childPath, key, ancestors);
        ancestors.pop();
        if (childMatches) {
          descendantMatches = true;
        }
      }
    }

    const currentPathMatches = labelMatches || valueMatches || descendantMatches;
    if (!currentPathMatches) return false;

    info.containsMatchPaths.add(path);
    ancestors.forEach((ancestorPath) => {
      info.containsMatchPaths.add(ancestorPath);
      info.expandedPaths.add(ancestorPath);
    });
    if (descendantMatches && isJsonContainer(currentValue)) {
      info.expandedPaths.add(path);
    }
    if (labelMatches) info.labelMatchPaths.add(path);
    if (valueMatches) info.valueMatchPaths.add(path);
    if (labelMatches || valueMatches) info.matchCount += 1;
    return true;
  };

  visit(value, '', null, []);
  return info;
};

const appendJsonRows = ({
  value,
  path,
  depth,
  label,
  labelType,
  trailingComma,
  isPathExpanded,
  searchInfo = EMPTY_SEARCH_INFO,
  rows,
}) => {
  if (!isJsonContainer(value)) {
    rows.push({
      type: 'value',
      value,
      depth,
      label,
      labelType,
      trailingComma,
      labelMatch: searchInfo.labelMatchPaths.has(path),
      valueMatch: searchInfo.valueMatchPaths.has(path),
    });
    return;
  }

  const isArray = Array.isArray(value);
  const isExpanded = isPathExpanded(path);
  const keys = isArray ? null : Object.keys(value);
  const entryCount = isArray ? value.length : keys.length;
  const visibleEntries = [];

  if (isExpanded) {
    for (let index = 0; index < entryCount; index += 1) {
      const key = isArray ? index : keys[index];
      const childPath = getJsonPath(path, key, isArray);
      if (!searchInfo.active || searchInfo.containsMatchPaths.has(childPath)) {
        visibleEntries.push({ key, childPath });
      }
    }
  }

  rows.push({
    type: 'open',
    path,
    depth,
    label,
    labelType,
    bracket: isArray ? '[' : '{',
    isExpanded,
    labelMatch: searchInfo.labelMatchPaths.has(path),
    valueMatch: searchInfo.valueMatchPaths.has(path),
  });

  if (isExpanded) {
    visibleEntries.forEach(({ key, childPath }, index) => {
      appendJsonRows({
        value: value[key],
        path: childPath,
        depth: depth + 1,
        label: String(key),
        labelType: isArray ? 'index' : 'key',
        trailingComma: index < visibleEntries.length - 1,
        isPathExpanded,
        searchInfo,
        rows,
      });
    });
  }

  rows.push({
    type: 'close',
    path,
    depth,
    bracket: isArray ? ']' : '}',
    trailingComma,
    labelMatch: false,
    valueMatch: false,
  });
};

const buildJsonRows = (value, isPathExpanded, searchInfo = EMPTY_SEARCH_INFO) => {
  const rows = [];
  appendJsonRows({
    value,
    path: '',
    depth: 0,
    label: null,
    labelType: null,
    trailingComma: false,
    isPathExpanded,
    searchInfo,
    rows,
  });
  return rows;
};

const highlightString = (value, searchTerm) => {
  if (!searchTerm) return value;

  const normalizedValue = value.toLowerCase();
  const normalizedSearchTerm = searchTerm.toLowerCase();
  const parts = [];
  let cursor = 0;
  let matchIndex = normalizedValue.indexOf(normalizedSearchTerm, cursor);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      parts.push(value.slice(cursor, matchIndex));
    }
    parts.push(
      <mark key={`${matchIndex}-${matchIndex + normalizedSearchTerm.length}`}>
        {value.slice(matchIndex, matchIndex + normalizedSearchTerm.length)}
      </mark>
    );
    cursor = matchIndex + normalizedSearchTerm.length;
    matchIndex = normalizedValue.indexOf(normalizedSearchTerm, cursor);
  }

  if (cursor === 0) return value;
  if (cursor < value.length) parts.push(value.slice(cursor));
  return parts;
};

const escapeJsonString = (value) => {
  const serialized = JSON.stringify(String(value));
  // JSON.stringify escapes normal control characters, while these Unicode
  // line separators should also never create a second visual row.
  return serialized
    .slice(1, -1)
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
};

const renderJsonScalar = (value, searchTerm) => {
  if (value === null) return <JsonNull>{highlightString('null', searchTerm)}</JsonNull>;
  if (typeof value === 'boolean') {
    return <JsonBoolean>{highlightString(value.toString(), searchTerm)}</JsonBoolean>;
  }
  if (typeof value === 'number') {
    return <JsonNumber>{highlightString(String(value), searchTerm)}</JsonNumber>;
  }
  if (typeof value === 'string') {
    return <JsonString>"{highlightString(escapeJsonString(value), searchTerm)}"</JsonString>;
  }
  return String(value);
};

const renderJsonLabel = (row, searchTerm) => {
  const labelValue = row.labelType === 'index'
    ? String(row.label)
    : escapeJsonString(row.label);
  const label = highlightString(labelValue, searchTerm);
  return row.labelType === 'index' ? <>{label}:</> : <>&quot;{label}&quot;:</>;
};

const JsonRow = React.memo(({ index, style, data }) => {
  const row = data.rows[index];
  const label = row.label === null || row.label === undefined
    ? null
    : <JsonKey>{renderJsonLabel(row, data.searchTerm)}</JsonKey>;
  const isContainerRow = row.type === 'open';

  return (
    <div
      style={{
        ...style,
        paddingLeft: `${row.depth * 20}px`,
        boxSizing: 'border-box',
        whiteSpace: 'pre',
        lineHeight: `${JSON_ROW_HEIGHT}px`,
        backgroundColor: row.labelMatch || row.valueMatch
          ? 'rgba(255, 193, 7, 0.16)'
          : undefined,
      }}
    >
      {label}
      {label && ' '}
      {isContainerRow ? (
        <>
          <JsonBracket>{row.bracket}</JsonBracket>
          <ExpandButton
            size="small"
            aria-label={row.isExpanded ? 'Collapse JSON branch' : 'Expand JSON branch'}
            onClick={() => data.toggleExpanded(row.path)}
          >
            {row.isExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </ExpandButton>
        </>
      ) : row.type === 'close' ? (
        <JsonBracket>{row.bracket}</JsonBracket>
      ) : (
        renderJsonScalar(row.value, data.searchTerm)
      )}
      {row.trailingComma && ','}
    </div>
  );
});

JsonRow.displayName = 'JsonRow';

const VirtualizedJsonRows = ({ rows, searchTerm, toggleExpanded }) => {
  const listContainerRef = useRef(null);
  const [listHeight, setListHeight] = useState(300);

  useEffect(() => {
    const listContainer = listContainerRef.current;
    if (!listContainer) return undefined;

    const updateListHeight = () => {
      setListHeight(Math.max(listContainer.clientHeight, 300));
    };
    updateListHeight();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const resizeObserver = new ResizeObserver(updateListHeight);
    resizeObserver.observe(listContainer);
    return () => resizeObserver.disconnect();
  }, []);

  const itemData = useMemo(() => ({
    rows,
    searchTerm,
    toggleExpanded,
  }), [rows, searchTerm, toggleExpanded]);

  return (
    <Box ref={listContainerRef} sx={{ height: '100%', minHeight: 300, width: '100%' }}>
      <FixedSizeList
        height={listHeight}
        width="100%"
        itemCount={rows.length}
        itemSize={JSON_ROW_HEIGHT}
        itemData={itemData}
        overscanCount={JSON_LIST_OVERSCAN}
        style={{ overflowX: 'auto', overflowY: 'auto' }}
      >
        {JsonRow}
      </FixedSizeList>
    </Box>
  );
};

const JSONViewer = ({ open, onClose, filename = '', eventId = null, filePath = '', embedded = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: rawJsonData,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.json_missing_source', 'File id or path is required'),
  });
  const jsonData = useMemo(() => {
    if (rawJsonData == null) return null;
    if (typeof rawJsonData !== 'string') return rawJsonData;
    try {
      return JSON.parse(rawJsonData);
    } catch {
      return rawJsonData;
    }
  }, [rawJsonData]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('formatted');
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const containerRef = useRef(null);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      // Keep the initial render bounded for large files. Nested branches can
      // still be opened individually or with the explicit Expand All action.
      setAllExpanded(false);
      setExpandedPaths(new Set(['']));
    }
  }, [open, eventId, filePath]);

  const downloadJson = () => {
    if (jsonData) {
      const content = JSON.stringify(jsonData, null, 2);
      const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename || 'data'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const copyToClipboard = () => {
    if (jsonData) {
      const content = JSON.stringify(jsonData, null, 2);
      navigator.clipboard.writeText(content);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Analyze JSON structure
  const analyzeJson = (obj) => {
    let stats = {
      objects: 0,
      arrays: 0,
      strings: 0,
      numbers: 0,
      booleans: 0,
      nulls: 0,
      totalKeys: 0,
      maxDepth: 0,
    };

    const analyze = (value, depth) => {
      stats.maxDepth = Math.max(stats.maxDepth, depth);

      if (value === null) {
        stats.nulls++;
      } else if (typeof value === 'boolean') {
        stats.booleans++;
      } else if (typeof value === 'number') {
        stats.numbers++;
      } else if (typeof value === 'string') {
        stats.strings++;
      } else if (Array.isArray(value)) {
        stats.arrays++;
        value.forEach((item) => analyze(item, depth + 1));
      } else if (typeof value === 'object') {
        stats.objects++;
        const keys = Object.keys(value);
        stats.totalKeys += keys.length;
        keys.forEach(key => analyze(value[key], depth + 1));
      }
    };

    analyze(obj, 0);
    return stats;
  };

  const stats = useMemo(() => (jsonData ? analyzeJson(jsonData) : null), [jsonData]);

  const searchInfo = useMemo(
    () => (jsonData == null ? EMPTY_SEARCH_INFO : buildJsonSearchInfo(jsonData, deferredSearchTerm)),
    [jsonData, deferredSearchTerm]
  );

  const isPathExpanded = useCallback((path) => (
    searchInfo.expandedPaths.has(path)
      || (allExpanded ? !expandedPaths.has(path) : expandedPaths.has(path))
  ), [allExpanded, expandedPaths, searchInfo]);

  const toggleExpanded = useCallback((path) => {
    setExpandedPaths((previousPaths) => {
      const newExpanded = new Set(previousPaths);
      if (newExpanded.has(path)) {
        newExpanded.delete(path);
      } else {
        newExpanded.add(path);
      }
      return newExpanded;
    });
  }, []);

  const expandAll = useCallback(() => {
    if (stats) {
      // Keep this as a mode instead of materializing every path in the file.
      setAllExpanded(true);
      setExpandedPaths(new Set());
    }
  }, [stats]);

  const collapseAll = () => {
    setAllExpanded(false);
    setExpandedPaths(new Set());
  };

  const jsonRows = useMemo(() => {
    if (jsonData == null || viewMode === 'raw') return [];
    return buildJsonRows(jsonData, isPathExpanded, searchInfo);
  }, [jsonData, viewMode, isPathExpanded, searchInfo]);

  const rawJson = useMemo(
    () => (jsonData == null ? '' : JSON.stringify(jsonData, null, 2)),
    [jsonData]
  );

  return (
    <ViewerDialogFrame
      embedded={embedded}
      DialogComponent={StyledDialog}
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <CodeIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.json_viewer_title')} - ${filename}` : t('viewer.json_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadJson} 
            disabled={!jsonData}
            size="small"
            title={t('viewer.download_json')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={copyToClipboard} 
            disabled={!jsonData}
            size="small"
            title="Copy to Clipboard"
          >
            <ContentCopyIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {stats && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${stats.objects} objects`}
              size="small"
              variant="outlined"
              icon={<DataObjectIcon />}
            />
            <Chip 
              label={`${stats.arrays} arrays`}
              size="small"
              variant="outlined"
              icon={<DataArrayIcon />}
            />
            <Chip 
              label={`Depth: ${stats.maxDepth}`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <JsonContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_json')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_json')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : jsonData ? (
            <JsonPanel>
              {/* Statistics Card */}
              {showStats && stats && (
                <StatsCard>
                  <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 } }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, lineHeight: 1.2 }}>
                      JSON Structure Analysis
                    </Typography>
                    <Grid container spacing={0.5}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="primary" sx={{ lineHeight: 1.15 }}>
                            {stats.objects}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Objects
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="primary" sx={{ lineHeight: 1.15 }}>
                            {stats.arrays}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Arrays
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="primary" sx={{ lineHeight: 1.15 }}>
                            {stats.strings}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Strings
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="primary" sx={{ lineHeight: 1.15 }}>
                            {stats.maxDepth}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Max Depth
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StatsCard>
              )}

              {/* Filter Row */}
              <FilterRow>
                <TextField
                  size="small"
                  placeholder={t('viewer.search_json_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                {searchInfo.active && (
                  <Typography
                    variant="caption"
                    color={searchInfo.matchCount > 0 ? 'text.secondary' : 'error'}
                  >
                    {searchInfo.matchCount > 0
                      ? t('viewer.json_search_matches', '{{count}} matches', {
                        count: searchInfo.matchCount,
                      })
                      : t('viewer.json_search_no_matches', 'No matches')}
                  </Typography>
                )}
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>View Mode</InputLabel>
                  <Select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    label="View Mode"
                  >
                    <MenuItem value="formatted">Formatted</MenuItem>
                    <MenuItem value="raw">Raw</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  startIcon={<FormatIndentIncreaseIcon />}
                  variant="outlined"
                  size="small"
                  onClick={expandAll}
                >
                  Expand All
                </Button>
                
                <Button
                  startIcon={<BlockIcon />}
                  variant="outlined"
                  size="small"
                  onClick={collapseAll}
                >
                  Collapse All
                </Button>
                
                <Button
                  startIcon={showStats ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setShowStats(!showStats)}
                >
                  {showStats ? 'Hide' : 'Show'} Stats
                </Button>
              </FilterRow>

              {/* JSON Viewer */}
              <JsonViewer>
                <JsonContent>
                  {viewMode === 'raw' ? (
                    <RawJsonContent>
                      {highlightString(rawJson, deferredSearchTerm.trim())}
                    </RawJsonContent>
                  ) : (
                    <VirtualizedJsonRows
                      rows={jsonRows}
                      searchTerm={deferredSearchTerm.trim()}
                      toggleExpanded={toggleExpanded}
                    />
                  )}
                </JsonContent>
              </JsonViewer>
            </JsonPanel>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_json_content')}
              </Typography>
            </Box>
          )}
        </JsonContainer>
      </ViewerDialogContent>
    </ViewerDialogFrame>
  );
};

export default React.memo(JSONViewer);
